import React, { useCallback, useEffect, useMemo, useState } from "react"

import CustomSelect, {
  SelectOption,
} from "../../../component/customSelect/customSelect"
import { listAllHierarchyItems } from "../../../api/subject"
import {
  HierarchyItem,
  DEFAULT_LEVEL_LABELS,
  enabledDepth,
  labelAt,
  normalizeLabels,
} from "../../Subjects/detail/hierarchyUtils"
import { isQuestionHierarchyLevelRequired } from "../utils/hierarchyValidation"

export type LevelFieldValues = {
  level_1: string
  level_2: string
  level_3: string
  level_4: string
}

export type LevelFieldNames = {
  level_1?: string | null
  level_2?: string | null
  level_3?: string | null
  level_4?: string | null
}

export type LevelFieldErrors = {
  level_1?: string
  level_2?: string
  level_3?: string
  level_4?: string
}

export const EMPTY_LEVEL_VALUES: LevelFieldValues = {
  level_1: "",
  level_2: "",
  level_3: "",
  level_4: "",
}

const LEVEL_KEYS: (keyof LevelFieldValues)[] = [
  "level_1",
  "level_2",
  "level_3",
  "level_4",
]

interface LevelSelectorsProps {
  subjectId: string | null
  levelLabels?: string[]
  values: LevelFieldValues
  names?: LevelFieldNames
  onChange: (values: LevelFieldValues, names: LevelFieldNames) => void
  errors?: LevelFieldErrors
  disabled?: boolean
  /** Render selectors inline (single column) instead of the default grid. */
  className?: string
}

/**
 * Cascading hierarchy selectors driven by a subject's configurable
 * ``level_labels``. Level 1 maps to topic; levels 2-4 are filtered by the
 * selected parent. Changing a level clears every deeper selection.
 */
const LevelSelectors: React.FC<LevelSelectorsProps> = ({
  subjectId,
  levelLabels,
  values,
  names,
  onChange,
  errors,
  disabled,
  className,
}) => {
  const labels = useMemo(
    () => normalizeLabels(levelLabels && levelLabels.length ? levelLabels : DEFAULT_LEVEL_LABELS),
    [levelLabels]
  )
  const depth = useMemo(() => enabledDepth(labels), [labels])

  const [options, setOptions] = useState<Record<number, SelectOption[]>>({
    1: [],
    2: [],
    3: [],
    4: [],
  })

  const parentForLevel = (level: number): string => {
    if (level <= 1) return ""
    return values[LEVEL_KEYS[level - 2]]
  }

  const setLevelOptions = (level: number, opts: SelectOption[]) =>
    setOptions(prev => ({ ...prev, [level]: opts }))

  const loadLevel = useCallback(
    async (level: number, parentId: string): Promise<SelectOption[]> => {
      if (!subjectId || (level > 1 && !parentId)) return []
      try {
        const items = (await listAllHierarchyItems(subjectId, {
          level,
          parent_id: level > 1 ? parentId : undefined,
        })) as HierarchyItem[]
        return items.map(item => ({ value: item.id, label: item.name }))
      } catch (error) {
        console.error(`Failed to load level ${level} items:`, error)
        return []
      }
    },
    [subjectId]
  )

  // Each level loads independently, keyed only on its own parent selection, so
  // a change at one level refetches just the child list below it (level 1 is
  // fetched once per subject rather than on every selection).
  useEffect(() => {
    let cancelled = false
    if (!subjectId || depth < 1) {
      setLevelOptions(1, [])
      return
    }
    loadLevel(1, "").then(opts => !cancelled && setLevelOptions(1, opts))
    return () => {
      cancelled = true
    }
  }, [subjectId, depth, loadLevel])

  useEffect(() => {
    let cancelled = false
    if (depth < 2 || !values.level_1) {
      setLevelOptions(2, [])
      return
    }
    loadLevel(2, values.level_1).then(opts => !cancelled && setLevelOptions(2, opts))
    return () => {
      cancelled = true
    }
  }, [values.level_1, depth, loadLevel])

  useEffect(() => {
    let cancelled = false
    if (depth < 3 || !values.level_2) {
      setLevelOptions(3, [])
      return
    }
    loadLevel(3, values.level_2).then(opts => !cancelled && setLevelOptions(3, opts))
    return () => {
      cancelled = true
    }
  }, [values.level_2, depth, loadLevel])

  useEffect(() => {
    let cancelled = false
    if (depth < 4 || !values.level_3) {
      setLevelOptions(4, [])
      return
    }
    loadLevel(4, values.level_3).then(opts => !cancelled && setLevelOptions(4, opts))
    return () => {
      cancelled = true
    }
  }, [values.level_3, depth, loadLevel])

  const handleSelect = (level: number, value: string, label: string) => {
    const next: LevelFieldValues = { ...values }
    const nextNames: LevelFieldNames = { ...(names ?? {}) }
    next[LEVEL_KEYS[level - 1]] = value
    nextNames[LEVEL_KEYS[level - 1]] = label || null
    // Clear deeper levels when an ancestor changes.
    for (let deeper = level + 1; deeper <= 4; deeper += 1) {
      next[LEVEL_KEYS[deeper - 1]] = ""
      nextNames[LEVEL_KEYS[deeper - 1]] = null
    }
    onChange(next, nextNames)
  }

  const valueOption = (level: number): SelectOption | null => {
    const id = values[LEVEL_KEYS[level - 1]]
    if (!id) return null
    const found = options[level]?.find(o => o.value === id)
    if (found) return found
    const fallbackName = names?.[LEVEL_KEYS[level - 1]]
    return { value: id, label: fallbackName || "" }
  }

  if (!subjectId) return null

  return (
    <>
      {Array.from({ length: depth }, (_, idx) => idx + 1).map(level => {
        const parentId = parentForLevel(level)
        const isDisabled = disabled || (level > 1 && !parentId)
        const placeholder =
          level > 1 && !parentId
            ? `Select ${labelAt(labels, level - 1).toLowerCase()} first`
            : `Select ${labelAt(labels, level).toLowerCase()}`
        const fieldError = errors?.[LEVEL_KEYS[level - 1]]
        return (
          <div className={className} key={level}>
            <label className="select_label">
              {labelAt(labels, level)}
              {isQuestionHierarchyLevelRequired(level, depth) ? " " : ""}
              {isQuestionHierarchyLevelRequired(level, depth) && <span>*</span>}
            </label>
            <div style={isDisabled ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
              <CustomSelect
                data={options[level] || []}
                value={valueOption(level)}
                placeholder={placeholder}
                onChange={selected => {
                  const option = selected as SelectOption | null
                  handleSelect(level, option?.value ?? "", option?.label ?? "")
                }}
              />
            </div>
            {fieldError && (
              <p className="error_message" style={{ marginTop: -5 }}>
                {fieldError}
              </p>
            )}
          </div>
        )
      })}
    </>
  )
}

export default LevelSelectors
